import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { Button, ButtonWrapper } from "../components/Button";
import Layout from "../components/Layout";
import { BaseLivlyApiResponse } from "../types/Base";
import { BASE_API_URL } from "../utils/constants";
import { Spinner } from "../components/Spinner";
import useLivlyUser from "../context/UserProvider";

export interface Notification {
  description: string;
  displayOrder: number;
  title: string;
  id: number;
  channels: Channel[];
  types?: NotificationType[];
}

export interface NotificationType {
  id: number;
  name: string;
  description: string;
  defaultEnabled: boolean;
  channels: Channel[];
}

export interface Channel {
  name: "Email" | "Push";
  canToggle: boolean;
  id: number;
  enabled: boolean;
}

interface LoaderData {
  notifications: Notification[];
}

async function getNotifications() {
  const result = await axios.get<BaseLivlyApiResponse<Notification[]>>(
    `${BASE_API_URL}/livly/users/me/notifications`
  );

  return result.data.Data;
}

const notificationsQuery = (leaseId: number) => ({
  queryKey: ["notifications", leaseId],
  queryFn: async () => getNotifications(),
});

async function patchNotifications(data: Notification[]) {
  const result = await axios.patch<BaseLivlyApiResponse<Notification[]>>(
    `${BASE_API_URL}/livly/users/me/notifications`,
    data
  );

  return result.data.Data;
}

const isCommunityFeedNotification = (notification: Notification): boolean =>
  notification.title.toLowerCase().includes("community feed");

const hasAnyChannelEnabled = (channels: Channel[]): boolean =>
  channels.some((ch) => ch.enabled);

function NotificationsPage() {
  const { user } = useLivlyUser();
  const queryClient = useQueryClient();

  const { mutate, isLoading: isSaving } = useMutation(patchNotifications);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { data, isLoading } = useQuery({
    ...notificationsQuery(user.propertyUnitLeaseId),
  });

  useEffect(() => {
    if (data) {
      setNotifications(data);
    }
  }, [data]);

  const onUpdateChannel = (id: number, channelId: number, enabled: boolean) => {
    const newNotifications = notifications.map((item) => {
      if (item.id === id) {
        const channels = item.channels.map((channel) => {
          if (channel.id === channelId) {
            return { ...channel, enabled };
          }
          return channel;
        });

        const parentDisabled =
          isCommunityFeedNotification(item) && !hasAnyChannelEnabled(channels);

        const types = parentDisabled
          ? item.types?.map((type) => ({
              ...type,
              channels: type.channels.map((ch) => ({ ...ch, enabled: false })),
            }))
          : item.types;

        return { ...item, channels, types };
      }
      return item;
    });

    setNotifications(newNotifications);
  };

  const onUpdateTypeChannel = (
    parentId: number,
    typeId: number,
    channelId: number,
    enabled: boolean
  ) => {
    const newNotifications = notifications.map((item) => {
      if (item.id === parentId) {
        const types = item.types?.map((type) => {
          if (type.id === typeId) {
            return {
              ...type,
              channels: type.channels.map((ch) =>
                ch.id === channelId ? { ...ch, enabled } : ch
              ),
            };
          }
          return type;
        });
        return { ...item, types };
      }
      return item;
    });

    setNotifications(newNotifications);
  };

  const onSubmit = () => {
    mutate(notifications, {
      onSuccess: () => {
        toast.success("Notifications updated!");
        queryClient.invalidateQueries(["notifications"]);
      },
      onError: () => {
        toast.error("There was an error");
      },
    });
  };

  return (
    <Layout
      title="Notifications"
      back={{ to: "../settings", label: "Settings" }}
    >
      {isLoading ? (
        <div className="flex items-center justify-center flex-1 my-56">
          <Spinner color="livly" size="xl" />
        </div>
      ) : (
        <>
          <ul className="">
            {notifications.map((not) => {
              const parentEnabled = hasAnyChannelEnabled(not.channels);

              return (
                <li key={not.id} className="py-4">
                  <p className="font-medium">{not.title}</p>
                  <p className="text-sm">{not.description}</p>
                  <ul className="pl-4 mt-4">
                    {not.channels.map((channel) => (
                      <li key={channel.id} className="py-1">
                        <label className="flex gap-4 text-sm">
                          <span className="w-24">{channel.name}</span>
                          <input
                            type="checkbox"
                            disabled={!channel.canToggle}
                            checked={channel.enabled}
                            onChange={(e) => {
                              onUpdateChannel(
                                not.id,
                                channel.id,
                                e.target.checked
                              );
                            }}
                          />
                        </label>
                      </li>
                    ))}
                  </ul>
                  {isCommunityFeedNotification(not) &&
                    not.types?.map((type) => (
                      <div
                        key={type.id}
                        className={`ml-4 mt-3 p-3 rounded-lg bg-gray-50 border-l-4 border-gray-300 max-w-[75%] ${
                          !parentEnabled ? "opacity-50" : ""
                        }`}
                      >
                        <p className="font-medium text-sm">{type.name}</p>
                        <p className="text-xs text-gray-500">
                          {type.description}
                        </p>
                        <ul className="pl-4 mt-2">
                          {type.channels.map((channel) => (
                            <li key={channel.id} className="py-1">
                              <label className="flex gap-4 text-sm">
                                <span className="w-24">{channel.name}</span>
                                <input
                                  type="checkbox"
                                  disabled={!parentEnabled || !channel.canToggle}
                                  checked={channel.enabled}
                                  onChange={(e) => {
                                    onUpdateTypeChannel(
                                      not.id,
                                      type.id,
                                      channel.id,
                                      e.target.checked
                                    );
                                  }}
                                />
                              </label>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                </li>
              );
            })}
          </ul>
          <ButtonWrapper>
            <Button onClick={onSubmit} disabled={isSaving}>
              {isSaving && <Spinner />}
              Save
            </Button>
          </ButtonWrapper>
        </>
      )}
    </Layout>
  );
}

export { NotificationsPage };
